import {
  ROTULO_DO_TIPO_DE_DESCONTO,
  type TipoDeDesconto,
  TIPOS_DE_DESCONTO,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  CalendarClock,
  CircleCheck,
  CircleSlash,
  EllipsisVertical,
  Hourglass,
  type LucideIcon,
  Pencil,
  Plus,
  Power,
  Search,
  TicketPercent,
  TriangleAlert,
} from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { type FiltroDeAtivo, vigenciaEmTexto } from '@/features/precos/api'
import {
  type Cupom,
  DialogoDeCupom,
  descontoEmTexto,
  hojeNaOperacao,
  type SituacaoDoCupom,
  situacaoDoCupom,
  useAtualizarCupom,
  useCupons,
  useDesligarCupom,
  usosEmTexto,
  usosRestantes,
} from '@/features/precos/dialogo-de-cupom'
import { errorMessage } from '@/lib/api'
import { SEM_VALOR } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { useValorAtrasado } from '@/lib/hooks'
import { usePermissao } from '@/lib/permissoes'

/**
 * Cupons de desconto.
 *
 * ---------------------------------------------------------------------------
 * "ATIVO" NÃO É UMA COLUNA, É UMA CONTA
 * ---------------------------------------------------------------------------
 * `ativo` é só o interruptor. Um cupom pode estar ligado e não valer nada:
 * porque a vigência acabou, porque ainda não começou, ou porque bateu o teto de
 * usos. Mostrar "Ativo" nesses três casos faria o gestor procurar bug na API
 * enquanto o cliente recebe "cupom inválido" na tela.
 *
 * Por isso a coluna de situação vem de `situacaoDoCupom`, que cruza os quatro
 * campos e devolve UMA palavra — e cada palavra pede uma ação diferente:
 * vencido se prorroga, agendado se espera, esgotado se amplia, desligado se
 * religa. Quem decide de verdade é a API, no fuso da operação; o selo é
 * informativo, e é por isso que "hoje" também sai do fuso da operação, não do
 * relógio do navegador.
 *
 * ---------------------------------------------------------------------------
 * PERCENTUAL NA TELA, PONTOS-BASE NA API
 * ---------------------------------------------------------------------------
 * A API guarda `valorEmPontosBase` (1500 = 15%). Esta tela nunca mostra pontos-
 * base e nunca os digita: a conversão nos dois sentidos vive em um lugar só,
 * `features/precos/dialogo-de-cupom`, feita sobre os dígitos da string. Um
 * segundo lugar que multiplicasse por 100 seria o primeiro a errar.
 *
 * ---------------------------------------------------------------------------
 * O AMARELO APARECE UMA VEZ
 * ---------------------------------------------------------------------------
 * No CTA "Novo cupom". Desconto é dinheiro, e dinheiro aqui é texto branco em
 * `tabular-money`; esgotado é âmbar semântico, não a cor da marca.
 */

const TAMANHO_DA_PAGINA = 20

/** Os dois estados do filtro booleano. `''` (todos) não entra na lista. */
const OPCOES_DE_ATIVO = ['true', 'false'] as const satisfies readonly FiltroDeAtivo[]

const ROTULO_DE_ATIVO: Readonly<Record<'true' | 'false', string>> = {
  true: 'Ligados',
  false: 'Desligados',
}

/**
 * Cada situação com cor, ícone e rótulo — cor nunca comunica sozinha.
 *
 * `VENCIDO` é cinza e não vermelho: prazo que acaba é o comportamento normal de
 * uma campanha, e toda promoção do ano passado está assim. Pintar de âmbar
 * encheria a lista de alertas que ninguém precisa atender e faria o âmbar
 * perder o significado onde ele importa — que é `ESGOTADO`, o único caso em que
 * a campanha ainda está no ar e o cliente vai levar "cupom inválido".
 *
 * Nenhuma variante `brand`: amarelo aqui é o CTA "Novo cupom", e uma coluna de
 * selos amarelos tiraria o olho justamente do botão que cria a campanha.
 *
 * Rótulo, ícone e variante moram no MESMO registro porque descrevem a mesma
 * situação: em dois mapas paralelos, acrescentar uma situação nova compila com
 * metade da resposta preenchida. O `satisfies` obriga os cinco casos.
 */
const SITUACAO = {
  VIGENTE: { rotulo: 'Vigente', icone: CircleCheck, variante: 'success' },
  AGENDADO: { rotulo: 'Agendado', icone: CalendarClock, variante: 'info' },
  VENCIDO: { rotulo: 'Vencido', icone: Hourglass, variante: 'neutral' },
  ESGOTADO: { rotulo: 'Esgotado', icone: TriangleAlert, variante: 'warning' },
  DESLIGADO: { rotulo: 'Desligado', icone: CircleSlash, variante: 'neutral' },
} as const satisfies Readonly<
  Record<
    SituacaoDoCupom,
    {
      readonly rotulo: string
      readonly icone: LucideIcon
      readonly variante: BadgeProps['variant']
    }
  >
>

function BadgeDeSituacaoDoCupom({
  situacao,
}: {
  readonly situacao: SituacaoDoCupom
}): ReactElement {
  const { rotulo, icone: Icone, variante } = SITUACAO[situacao]

  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {rotulo}
    </Badge>
  )
}

export function CuponsPage(): ReactElement {
  const podeCriar = usePermissao('coupon.create')
  const podeEditar = usePermissao('coupon.update')
  const podeDesligar = usePermissao('coupon.delete')

  const [pagina, setPagina] = useState(1)
  const [buscaDigitada, setBuscaDigitada] = useState('')
  const [tipo, setTipo] = useState<TipoDeDesconto | ''>('')
  const [ativo, setAtivo] = useState<FiltroDeAtivo>('')
  const [vigenteEm, setVigenteEm] = useState('')
  const [apenasDisponiveis, setApenasDisponiveis] = useState<FiltroDeAtivo>('')

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Cupom | undefined>(undefined)

  const busca = useValorAtrasado(buscaDigitada)

  /**
   * Uma leitura só do relógio por render. Se cada linha chamasse
   * `hojeNaOperacao()`, uma tabela aberta na virada da meia-noite poderia
   * classificar as primeiras linhas em um dia e as últimas no seguinte.
   */
  const hoje = hojeNaOperacao()

  const filtros = useMemo(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      busca,
      tipo,
      ativo,
      vigenteEm,
      apenasDisponiveis,
    }),
    [pagina, busca, tipo, ativo, vigenteEm, apenasDisponiveis],
  )

  const consulta = useCupons(filtros)
  const atualizar = useAtualizarCupom()
  const desligar = useDesligarCupom()

  const escrevendo = atualizar.isPending || desligar.isPending

  const temFiltro =
    busca.length > 0 || tipo !== '' || ativo !== '' || vigenteEm !== '' || apenasDisponiveis !== ''

  function limparFiltros(): void {
    setBuscaDigitada('')
    setTipo('')
    setAtivo('')
    setVigenteEm('')
    setApenasDisponiveis('')
    setPagina(1)
  }

  function abrirCriacao(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  function abrirEdicao(cupom: Cupom): void {
    setEmEdicao(cupom)
    setDialogoAberto(true)
  }

  /**
   * Desligar NÃO apaga. Apagar levaria junto, em cascata, todos os resgates — o
   * registro de quem ganhou desconto em quais reservas, que é o que o
   * financeiro procura quando o faturamento não bate com a tabela.
   */
  function desligarCupom(cupom: Cupom): void {
    desligar.mutate(cupom.id, {
      onSuccess: () => {
        toast.success(`${cupom.codigo} foi desligado`, {
          description: 'Quem digitar o código recebe “cupom inválido”. Os resgates ficam.',
        })
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível desligar o cupom', { description: errorMessage(causa) })
      },
    })
  }

  /** Religar é um `PATCH` com `ativo: true` — o desligamento não apagou nada. */
  function religar(cupom: Cupom): void {
    atualizar.mutate(
      { id: cupom.id, dados: { ativo: true } },
      {
        onSuccess: (salvo) => {
          toast.success(`${salvo.codigo} voltou ao ar`, {
            description:
              situacaoDoCupom(salvo, hoje) === 'VIGENTE'
                ? `Desconto de ${descontoEmTexto(salvo)}.`
                : 'O interruptor voltou, mas a vigência ou os usos ainda não deixam o cupom valer.',
          })
        },
        onError: (causa: unknown) => {
          toast.error('Não foi possível religar o cupom', { description: errorMessage(causa) })
        },
      },
    )
  }

  const colunas = useMemo<ColumnDef<Cupom>[]>(
    () => [
      {
        id: 'codigo',
        header: 'Código',
        accessorFn: (linha: Cupom) => linha.codigo,
        cell: ({ row }) => {
          const cupom: Cupom = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-semibold tracking-wide tabular-money text-foreground">
                {cupom.codigo}
              </p>
              <p className="truncate text-xs text-fg-secondary">
                {ROTULO_DO_TIPO_DE_DESCONTO[cupom.tipo]}
              </p>
            </div>
          )
        },
      },
      {
        id: 'desconto',
        header: 'Desconto',
        meta: { alinhamento: 'direita' },
        // Percentual e valor fixo não são comparáveis entre si: ordenar "15%" com
        // "R$ 150,00" produziria uma ordem que parece significar algo e não
        // significa. Quem quer comparar filtra por tipo antes.
        enableSorting: false,
        cell: ({ row }) => {
          const cupom: Cupom = row.original

          return (
            // `money-cell` alinha à DIREITA; o piso da reserva acompanha, senão
            // as duas metades do mesmo desconto ficam em bordas opostas da
            // célula.
            <div className="min-w-36 text-right">
              <span className="money-cell block text-foreground">{descontoEmTexto(cupom)}</span>
              {cupom.valorMinimoDaReserva === null ? null : (
                <span className="block text-xs tabular-money text-fg-secondary">
                  a partir de {formatarMoedaDoWire(cupom.valorMinimoDaReserva)}
                </span>
              )}
            </div>
          )
        },
      },
      {
        id: 'vigencia',
        header: 'Vigência',
        accessorFn: (linha: Cupom) => linha.vigenteDe,
        cell: ({ row }) => {
          const cupom: Cupom = row.original

          return (
            <span className="whitespace-nowrap text-sm tabular-money text-fg-secondary">
              {vigenciaEmTexto(cupom.vigenteDe, cupom.vigenteAte)}
            </span>
          )
        },
      },
      {
        id: 'usos',
        header: 'Usos',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: Cupom) => linha.usosRealizados,
        cell: ({ row }) => {
          const cupom: Cupom = row.original
          const restantes = usosRestantes(cupom)

          return (
            <div className="min-w-32 text-right">
              <span className="money-cell block text-foreground">{usosEmTexto(cupom)}</span>
              <span className="block text-xs tabular-money text-fg-secondary">
                {restantes === null ? 'sem teto' : `${String(restantes)} restantes`}
                {cupom.usosPorConta === null ? '' : ` · ${String(cupom.usosPorConta)} por conta`}
              </span>
            </div>
          )
        },
      },
      {
        id: 'situacao',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => (
          <BadgeDeSituacaoDoCupom situacao={situacaoDoCupom(row.original, hoje)} />
        ),
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const cupom: Cupom = row.original

          // A ação disponível depende da SITUAÇÃO da linha, não só da permissão:
          // religar só existe em cupom desligado, desligar só em cupom no ar.
          const podeReligar = podeEditar && !cupom.ativo
          const podeTirarDoAr = podeDesligar && cupom.ativo

          if (!podeEditar && !podeTirarDoAr) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações do cupom ${cupom.codigo}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                {podeEditar ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      abrirEdicao(cupom)
                    }}
                  >
                    <Pencil aria-hidden="true" />
                    Editar cupom
                  </DropdownMenuItem>
                ) : null}

                {podeReligar ? (
                  <DropdownMenuItem
                    // Uma escrita por vez: sem isto o segundo clique dispararia
                    // outra requisição e outro toast dizendo a mesma coisa.
                    disabled={escrevendo}
                    onSelect={() => {
                      religar(cupom)
                    }}
                  >
                    <Power aria-hidden="true" />
                    Religar cupom
                  </DropdownMenuItem>
                ) : null}

                {podeTirarDoAr ? (
                  <>
                    {podeEditar ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      disabled={escrevendo}
                      onSelect={() => {
                        desligarCupom(cupom)
                      }}
                    >
                      <CircleSlash aria-hidden="true" />
                      Desligar cupom
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda o
    // conjunto de colunas são as permissões, a escrita em curso e o dia — que
    // decide a situação exibida em toda linha.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar, podeDesligar, escrevendo, hoje],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Cupons</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            Campanhas de desconto. O código é sempre gravado em maiúsculas e sem espaço — e não se
            edita depois, porque já foi impresso em campanha, e-mail e link.
          </p>
        </div>

        {podeCriar ? (
          <Button onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Novo cupom
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="filtro-busca-cupons">Buscar</Label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2
                text-fg-muted"
            />
            <Input
              id="filtro-busca-cupons"
              type="search"
              value={buscaDigitada}
              onChange={(evento) => {
                setPagina(1)
                setBuscaDigitada(evento.target.value)
              }}
              placeholder="Código do cupom"
              // O servidor normaliza a busca do mesmo jeito que normaliza o
              // cadastro; digitar minúsculo aqui acha o cupom do mesmo jeito.
              className="pl-9 uppercase"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-tipo-cupons">Tipo de desconto</Label>
          <Select
            id="filtro-tipo-cupons"
            value={tipo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setTipo(TIPOS_DE_DESCONTO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {TIPOS_DE_DESCONTO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_TIPO_DE_DESCONTO[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-ativo-cupons">Interruptor</Label>
          <Select
            id="filtro-ativo-cupons"
            value={ativo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setAtivo(OPCOES_DE_ATIVO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {OPCOES_DE_ATIVO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DE_ATIVO[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-vigente-em">Vigente em</Label>
          <Input
            id="filtro-vigente-em"
            type="date"
            value={vigenteEm}
            onChange={(evento) => {
              setPagina(1)
              setVigenteEm(evento.target.value)
            }}
            className="tabular-money"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-disponiveis-cupons">Usos</Label>
          <Select
            id="filtro-disponiveis-cupons"
            value={apenasDisponiveis}
            onChange={(evento) => {
              setPagina(1)
              // Só o `'true'` é oferecido: `'false'` significaria "só os
              // esgotados", que a API não sabe responder — ela trata qualquer
              // coisa diferente de `true` como "sem restrição".
              setApenasDisponiveis(evento.target.value === 'true' ? 'true' : '')
            }}
          >
            <option value="">Todos os cupons</option>
            <option value="true">Só com uso disponível</option>
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Cupons de desconto"
        estadoVazio={
          <EmptyState
            icone={TicketPercent}
            titulo={temFiltro ? 'Nenhum cupom com esses filtros' : 'Nenhum cupom criado'}
            descricao={
              temFiltro
                ? 'Ajuste o código, o tipo ou a data de vigência para ver outros resultados.'
                : 'Uma campanha começa aqui: código, desconto, prazo e quantos usos ela aguenta. Sem teto de usos, um cupom vazado desconta para sempre.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={abrirCriacao}>
                  <Plus aria-hidden="true" />
                  Novo cupom
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeCupom
        aberto={dialogoAberto}
        cupom={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />
    </div>
  )
}
